import { clear, createStore, del, get, keys, set } from "idb-keyval";

import type { CanvasMeta, CanvasScene, Folder, AuthUser } from "./libraryApi";

// Dedicated IndexedDB store for PizzarWeb's offline-first cache. Persists across
// reboots so the user can keep editing existing boards with no connection and
// sync when it returns.
const store = createStore("pizzarweb-offline", "kv");

const SCENE_PREFIX = "scene:";
const PENDING_PREFIX = "pending:";
const LIBRARY_KEY = "library";

export interface CachedLibrary {
  folders: Folder[];
  canvases: CanvasMeta[];
}

// --- Library (folders + canvases) for the offline sidebar -----------------
export const cacheLibrary = (folders: Folder[], canvases: CanvasMeta[]) =>
  set(LIBRARY_KEY, { folders, canvases }, store).catch(() => {});

export const getCachedLibrary = (): Promise<CachedLibrary | undefined> =>
  get<CachedLibrary>(LIBRARY_KEY, store).catch(() => undefined);

// Wipe ALL offline data (library cache, scene caches, pending scenes, op-log).
// Used on a user switch so one account's boards never leak into another's
// session on a shared browser.
export const clearAllOfflineData = (): Promise<void> =>
  clear(store).catch(() => {});

// --- Per-canvas scene cache (latest known content) ------------------------
export const cacheScene = (id: string, scene: CanvasScene) =>
  set(SCENE_PREFIX + id, scene, store).catch(() => {});

export const getCachedScene = (id: string): Promise<CanvasScene | undefined> =>
  get<CanvasScene>(SCENE_PREFIX + id, store).catch(() => undefined);

export const clearCachedScene = (id: string) =>
  del(SCENE_PREFIX + id, store).catch(() => {});

// --- Pending changes queue (not yet synced to the backend) ----------------
export const setPending = (id: string, scene: CanvasScene) =>
  set(PENDING_PREFIX + id, scene, store).catch(() => {});

export const getPendingScene = (id: string): Promise<CanvasScene | undefined> =>
  get<CanvasScene>(PENDING_PREFIX + id, store).catch(() => undefined);

export const clearPending = (id: string) =>
  del(PENDING_PREFIX + id, store).catch(() => {});

export const getPendingIds = async (): Promise<string[]> => {
  try {
    const all = (await keys(store)) as string[];
    return all
      .filter((k) => typeof k === "string" && k.startsWith(PENDING_PREFIX))
      .map((k) => k.slice(PENDING_PREFIX.length));
  } catch {
    return [];
  }
};

// --- Op-log: ordered, idempotent structural mutations pending sync ---------
// Each op carries client-generated ids so replaying it on the server (which
// accepts those ids idempotently) is safe and never duplicates.
export type LibraryOp =
  | {
      kind: "createFolder";
      id: string;
      name: string;
      parent_id: string | null;
      position: number;
    }
  | {
      kind: "createCanvas";
      id: string;
      name: string;
      folder_id: string | null;
      position: number;
      canvas_type?: string;
      page_style?: string;
    }
  | {
      kind: "updateFolder";
      id: string;
      body: Partial<{
        name: string;
        parent_id: string | null;
        position: number;
      }>;
    }
  | {
      kind: "updateCanvas";
      id: string;
      body: Partial<{
        name: string;
        folder_id: string | null;
        position: number;
        description: string | null;
        canvas_type: string;
        page_style: string | null;
      }>;
    }
  | { kind: "deleteFolder"; id: string }
  | { kind: "deleteCanvas"; id: string };

const OPS_KEY = "ops";

export const getOps = (): Promise<LibraryOp[]> =>
  get<LibraryOp[]>(OPS_KEY, store)
    .then((o) => o ?? [])
    .catch(() => []);

export const setOps = (ops: LibraryOp[]) =>
  set(OPS_KEY, ops, store).catch(() => {});

export const enqueueOp = async (op: LibraryOp) => {
  const ops = await getOps();
  ops.push(op);
  await setOps(ops);
};

// --- Cached auth user (lets a previously-authenticated user in offline) ----
const AUTH_KEY = "pw-cached-user";

export const cacheAuthUser = (user: AuthUser) => {
  try {
    localStorage.setItem(AUTH_KEY, JSON.stringify(user));
  } catch {
    /* ignore */
  }
};

export const getCachedAuthUser = (): AuthUser | null => {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
};

export const clearCachedAuthUser = () => {
  try {
    localStorage.removeItem(AUTH_KEY);
  } catch {
    /* ignore */
  }
};
